import React, { useEffect, useMemo, useState } from "react";
import {
  createAhaModule, createAhaProduct, getStandardCourseDraft, linkStandardCourseAhaProduct, listStandardCourses,
  startStandardCourseVersion, updateStandardCourseItem,
} from "../../../api/apiClient";
import { Check, Modal, Notice, Toggle } from "../ui/AdminUi";
import { I } from "../ui/icons";
import { KEY_PATTERN, MODULE_ID_PATTERN, PRODUCT_NUMBER_PATTERN, serverMessage, suggestKey } from "../catalogFields";
import SessionModuleMapping from "./integrations/SessionModuleMapping";
import "./integrations/integrations.css";

// Same order as the design. Each is a family the catalog already groups by (see familyOf).
const CERT_TYPES = ["BLS", "ACLS", "PALS", "Heartsaver", "ASLS"];
const EDITIONS = ["2025", "2020", "2015"];
const GROUPS = ["A", "B", "C"];
const REQUIRED = "req";

/** What a rule means to a student: required, or one of a choose-one group. Maps to the catalog's exclusive group. */
const groupOf = (rule) => (rule === REQUIRED ? null : rule);

/**
 * Add an AHA product, or a module, from the Standard courses catalog. Four steps in one dialog: the certificate standard,
 * the certification type, the details, and the modules with a rule for each. A product can also be linked to a standard
 * course in the same step. Modules typed here are created just before the product, so cancelling creates nothing.
 */
export default function AddProductDialog({ open, modules, products, environment, onSaved, onClose }) {
  const [cert, setCert] = useState(CERT_TYPES[0]);
  const [kind, setKind] = useState("Product");
  const [name, setName] = useState("");
  const [edition, setEdition] = useState(EDITIONS[0]);
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [number, setNumber] = useState("");
  const [requiresSubcourse, setRequiresSubcourse] = useState(false);
  const [rules, setRules] = useState({});
  const [pending, setPending] = useState([]);
  const [newName, setNewName] = useState("");
  const [newId, setNewId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [courses, setCourses] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState([]);
  // The linked course draft sessions and which of this product modules each one teaches.
  const [sessions, setSessions] = useState([]);
  const [mapping, setMapping] = useState({});
  const [mapState, setMapState] = useState("idle");
  const [mapError, setMapError] = useState(null);

  const isModule = kind === "Module";

  useEffect(() => {
    if (!open) return undefined;
    setCert(CERT_TYPES[0]); setKind("Product"); setName(`${CERT_TYPES[0]} `); setEdition(EDITIONS[0]); setKey(""); setKeyTouched(false);
    setNumber(""); setRequiresSubcourse(false); setRules({}); setPending([]); setNewName(""); setNewId(""); setCourseId("");
    setError(null); setBusy(false); setCreated([]); setSessions([]); setMapping({}); setMapState("idle"); setMapError(null);
    let cancelled = false;
    listStandardCourses()
      .then((list) => { if (!cancelled) setCourses((Array.isArray(list) ? list : []).filter((c) => c.status !== "RETIRED")); })
      .catch(() => { if (!cancelled) setCourses([]); });
    return () => { cancelled = true; };
  }, [open]);

  const loadSessions = (id) => {
    setMapState("loading");
    setMapError(null);
    return getStandardCourseDraft(Number(id))
      .then((draft) => {
        const list = draft?.sessions || [];
        setSessions(list);
        setMapping(Object.fromEntries(list.map((x) => [x.id, x.ahaModuleKeys || []])));
        setMapState("ready");
      })
      .catch((e) => {
        // 409: a published course has no draft until a new version is started.
        if (e?.response?.status === 409) setMapState("needsDraft");
        else { setMapError(serverMessage(e, "The course sessions could not be loaded.")); setMapState("error"); }
      });
  };

  useEffect(() => {
    setSessions([]); setMapping({}); setMapError(null);
    if (!open || !courseId || isModule) { setMapState("idle"); return; }
    loadSessions(courseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, courseId, isModule]);

  const startDraft = () => {
    setBusy(true);
    startStandardCourseVersion(Number(courseId))
      .then(() => loadSessions(courseId))
      .catch((e) => { setMapError(serverMessage(e, "A new draft version could not be started.")); setMapState("error"); })
      .finally(() => setBusy(false));
  };

  const choose = (next) => {
    // Seed the name with the certification so the product lands in that family, unless the admin has typed their own.
    if (!name.trim() || name.trim() === cert) setName(`${next} `);
    setCert(next);
    if (!keyTouched) setKey(suggestKey(!name.trim() || name.trim() === cert ? next : name, edition));
  };
  const changeName = (value) => { setName(value); if (!keyTouched) setKey(suggestKey(value, isModule ? "" : edition)); };
  const changeEdition = (value) => { setEdition(value); if (!keyTouched) setKey(suggestKey(name, value)); };

  const numberTaken = !isModule && number.trim() && (products || []).find((p) => p.environment?.productNumber === number.trim());
  const numberOk = isModule ? MODULE_ID_PATTERN.test(number.trim()) : PRODUCT_NUMBER_PATTERN.test(number.trim());
  const valid = name.trim().length > 0 && KEY_PATTERN.test(key) && numberOk && !numberTaken;

  const library = useMemo(() => [
    ...(modules || []).filter((m) => m.active).map((m) => ({ key: m.key, name: m.name, isNew: false })),
    ...pending.map((m) => ({ key: m.key, name: m.name, isNew: true })),
  ], [modules, pending]);

  const picked = Object.entries(rules);
  const required = picked.filter(([, r]) => r === REQUIRED).map(([k]) => library.find((m) => m.key === k)?.name || k);
  const groups = GROUPS.map((g) => [g, picked.filter(([, r]) => r === g).map(([k]) => library.find((m) => m.key === k)?.name || k)]).filter(([, l]) => l.length);

  // The product modules as the mapping step offers them: the picked ones, with their choose-one group.
  const pickedModules = picked.map(([k, rule]) => ({ key: k, name: library.find((m) => m.key === k)?.name || k, group: groupOf(rule) }));
  const changeMapping = (sessionId, keys) => setMapping((m) => ({ ...m, [sessionId]: keys }));

  const toggleModule = (k) => setRules((r) => { const next = { ...r }; if (next[k]) delete next[k]; else next[k] = REQUIRED; return next; });

  const newKey = suggestKey(newName, "");
  const newKeyTaken = [...(modules || []), ...pending].some((m) => m.key === newKey);
  const canAddModule = newName.trim() && KEY_PATTERN.test(newKey) && MODULE_ID_PATTERN.test(newId.trim()) && !newKeyTaken;
  const addPending = () => {
    setPending((p) => [...p, { key: newKey, name: newName.trim(), moduleId: newId.trim() }]);
    setRules((r) => ({ ...r, [newKey]: REQUIRED }));
    setNewName(""); setNewId("");
  };

  /** Saves the changed sessions of the course draft. Returns how many were saved, or the first error. */
  const saveMapping = async () => {
    if (mapState !== "ready") return { count: 0 };
    let count = 0;
    try {
      for (const s of sessions) {
        const next = mapping[s.id] || [];
        const before = s.ahaModuleKeys || [];
        if (next.length === before.length && next.every((k) => before.includes(k))) continue;
        // Sequential: each session save is its own request on the course draft.
        // eslint-disable-next-line no-await-in-loop
        await updateStandardCourseItem(Number(courseId), "sessions", s.id, { ahaModuleKeys: next });
        count++;
      }
    } catch (e) {
      return { count, error: serverMessage(e, "A session could not be saved.") };
    }
    return { count };
  };

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    let step = "module";
    try {
      if (isModule) {
        await createAhaModule({ key, name: name.trim(), moduleId: number.trim() });
        onSaved(`${name.trim()} module added. Add it to a product with Edit product, then verify it.`);
        return;
      }
      const made = [...created];
      for (const m of pending.filter((x) => rules[x.key] && !made.includes(x.key))) {
        // Sequential on purpose: each create is audited, and a refused one must stop before the product exists.
        // eslint-disable-next-line no-await-in-loop
        await createAhaModule({ key: m.key, name: m.name, moduleId: m.moduleId });
        made.push(m.key);
        setCreated([...made]);
      }
      step = "product";
      await createAhaProduct({
        key, name: name.trim(), edition: edition.trim(), productNumber: number.trim(), requiresSubcourse,
        links: picked.map(([k, rule]) => ({ moduleKey: k, exclusiveGroup: groupOf(rule) })),
      });
      if (courseId) {
        try {
          await linkStandardCourseAhaProduct(Number(courseId), key);
        } catch (e) {
          onSaved(`${name.trim()} added, but it could not be linked to the course: ${serverMessage(e, "link it from the Catalog.")}`);
          return;
        }
      }
      const mapped = courseId ? await saveMapping() : { count: 0 };
      if (mapped.error) {
        onSaved(`${name.trim()} added and linked, but the session modules could not be saved: ${mapped.error} Map them on the course Sessions tab.`);
        return;
      }
      const applied = mapped.count ? `; modules mapped on ${mapped.count} session${mapped.count === 1 ? "" : "s"} of the course draft. Publish the course to apply them` : "";
      onSaved(`${name.trim()} added${courseId ? " and linked" : ""}${applied}. Verify it next.`);
    } catch (e) {
      const fallback = step === "module" ? "A module could not be created." : "The product could not be saved.";
      setError(serverMessage(e, fallback));
    } finally {
      setBusy(false);
    }
  };

  const noun = isModule ? "module" : "product";
  return (
    <Modal open={open} wide title={`Add ${noun}`} labelId="add-product-title" onClose={() => !busy && onClose()}
      footer={(
        <>
          <span className="note">Recorded in the audit log.</span>
          <button type="button" className="b ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="b pri" disabled={!valid || busy} onClick={submit}><I.plus s={13} />{busy ? "Creating…" : `Create ${noun}`}</button>
        </>
      )}
    >
      <Notice kind={environment === "PROD" ? "prod" : "info"}>
        Shared by every training center. Created in <b>AHA {environment}</b> only, not verified and inactive until you verify and activate it.
      </Notice>

      <section className="ap-sec">
        <h4><span className="ap-n">1</span>Certificate standard</h4>
        <div className="ap-std" role="radiogroup" aria-label="Certificate standard">
          <button type="button" role="radio" aria-checked="true" className="ap-card on">
            <span className="ap-card-h"><span className="ap-rad on" /><b>AHA</b></span>
            <span className="ap-card-n">AHA eCards</span>
            <span className="ap-card-f">Editions · Modules · Subcourses</span>
          </button>
        </div>
      </section>

      <section className="ap-sec">
        <h4><span className="ap-n">2</span>Certification type</h4>
        <div className="ap-chips">
          {CERT_TYPES.map((c) => (
            <button key={c} type="button" className={`fchip${cert === c ? " on" : ""}`} aria-pressed={cert === c} onClick={() => choose(c)}>
              {cert === c && <I.check s={12} />}{c}
            </button>
          ))}
        </div>
        <div className="field" style={{ marginTop: 4 }}>
          What are you adding?
          <div className="seg" role="radiogroup" aria-label="What are you adding" style={{ justifySelf: "start" }}>
            {[["Product", "Product"], ["Module", "Module of a product"]].map(([value, label]) => (
              <button key={value} type="button" role="radio" aria-checked={kind === value} className={kind === value ? "on" : ""} onClick={() => setKind(value)}>{label}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="ap-sec">
        <h4><span className="ap-n">3</span>{isModule ? "Module" : "Product"} details</h4>
        <div className={isModule ? "g2" : "ap-g3"}>
          <label className="field">Name
            <span className="inp"><input value={name} onChange={(e) => changeName(e.target.value)} placeholder={isModule ? "e.g. Megacode test" : `e.g. ${cert} Provider`} autoFocus /></span>
          </label>
          {!isModule && (
            <label className="field">Edition
              <span className="inp"><input list="ap-editions" value={edition} onChange={(e) => changeEdition(e.target.value)} placeholder="2025" /></span>
              <datalist id="ap-editions">{EDITIONS.map((x) => <option key={x} value={x} />)}</datalist>
            </label>
          )}
          <label className="field">{isModule ? "Module key" : "Product key"}
            <span className={`inp${key && !KEY_PATTERN.test(key) ? " bad" : ""}`}>
              <input className="mono" value={key} onChange={(e) => { setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_")); setKeyTouched(true); }}
                placeholder={`${cert.toUpperCase()}_PROVIDER_2025`} />
            </span>
            <span className="h">Starts with a letter; capital letters, digits and underscores. Locked after creation.</span>
          </label>
        </div>
        <label className="field">{isModule ? "AHA module ID" : "AHA product number"}
          <span className={`inp${(number && !numberOk) || numberTaken ? " bad" : ""}`}>
            <input className="mono" value={number} onChange={(e) => setNumber(e.target.value)} placeholder={isModule ? "35" : "25-3002"} />
          </span>
          {numberTaken
            ? <span className="err"><I.stop s={12} />Already in the catalog as {numberTaken.name}</span>
            : <span className="h">{isModule ? "Digits only, for AHA " : "For example 25-3002. Check it against AHA documentation for AHA "}{environment}.</span>}
        </label>
        {!isModule && (
          <Toggle checked={requiresSubcourse} onChange={setRequiresSubcourse} label="Requires a subcourse"
            hint="Centers pick a delivery format (for example blended or instructor-led) when they schedule a class." />
        )}
      </section>

      {!isModule && (
        <section className="ap-sec">
          <h4><span className="ap-n">4</span>Modules</h4>
          <p className="sub" style={{ margin: 0, fontSize: 12.5 }}>
            Set a rule for each module you select: required, or one of a choose-one group. A product without modules is issued as a whole.
          </p>
          <div className="panel ap-mods">
            {library.length === 0 && <div className="state" style={{ padding: 18 }}><b>No modules yet</b><p>Add the first one below.</p></div>}
            {library.map((m, i) => {
              const rule = rules[m.key];
              return (
                <div key={m.key} className={`ap-mod${rule ? " on" : ""}`} data-testid={`ap-mod-${m.key}`} style={{ borderTop: i ? "1px solid var(--ga-line)" : 0 }}>
                  <Check on={Boolean(rule)} label={m.name} onClick={() => toggleModule(m.key)} />
                  <span className="ap-mod-n" onClick={() => toggleModule(m.key)}>{m.name}{m.isNew && <span className="chip info" style={{ marginLeft: 8 }}>New</span>}</span>
                  <select className="sel" aria-label={`Rule for ${m.name}`} disabled={!rule} value={rule || REQUIRED}
                    onChange={(e) => setRules((r) => ({ ...r, [m.key]: e.target.value }))}>
                    <option value={REQUIRED}>Required</option>
                    {GROUPS.map((g) => <option key={g} value={g}>Choose one · group {g}</option>)}
                  </select>
                </div>
              );
            })}
            <div className="ap-mod ap-new" style={{ borderTop: library.length ? "1px solid var(--ga-line)" : 0 }}>
              <I.plus s={15} />
              <span className="inp" style={{ flex: 2, height: 34 }}>
                <input aria-label="New module name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={`New AHA module for ${cert}`} />
              </span>
              <span className="inp" style={{ flex: 1, height: 34 }}>
                <input className="mono" aria-label="New module AHA ID" value={newId} onChange={(e) => setNewId(e.target.value)} placeholder="AHA module ID" />
              </span>
              <button type="button" className="b sec sm" disabled={!canAddModule} onClick={addPending}>Add module</button>
            </div>
          </div>
          {newName.trim() && newKeyTaken && <span className="err"><I.stop s={12} />A module with the key {newKey} already exists. Pick it from the list instead.</span>}
          {picked.length > 0 && (
            <div className="ap-sum" data-testid="ap-summary">
              <span className="eyebrow">Students complete</span>
              {required.length > 0 && <div><b>All of:</b> {required.join(", ")}</div>}
              {groups.map(([g, list]) => (
                <div key={g}><b>{`One of (group ${g}):`}</b> {list.join(" or ")}
                  {list.length < 2 && <span className="ap-warn"><I.alert s={12} />A choose-one group needs at least two modules</span>}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {isModule && <Notice kind="neutral">A module belongs to products. After you create it, add it to a product with Edit product.</Notice>}

      {!isModule && (
        <label className="field">Link to standard course
          <select className="sel" value={courseId} onChange={(e) => setCourseId(e.target.value)}>
            <option value="">No course yet</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.title}{c.courseNumber ? ` · ${c.courseNumber}` : ""}</option>)}
          </select>
          <span className="h">Optional. You can link it later from the Catalog or the course's Integrations tab.</span>
        </label>
      )}

      {!isModule && courseId && picked.length > 0 && (
        <section className="ap-sec">
          <h4><span className="ap-n">5</span>Modules in the course sessions</h4>
          <p className="sub" style={{ margin: 0, fontSize: 12.5 }}>
            Tick the modules each session teaches. One session can cover several: a student eCard still carries every
            individual module of the sessions they attended.
          </p>
          <SessionModuleMapping modules={pickedModules} sessions={sessions} mapping={mapping} onChange={changeMapping}
            state={mapState === "idle" ? "loading" : mapState} error={mapError} onStartDraft={startDraft} busy={busy} />
        </section>
      )}

      {error && <Notice kind="bad">{error}</Notice>}
    </Modal>
  );
}
